import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { EventLogTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { PermissionGuard } from '~/core/PermissionGuard';
import { useNotifyNewCommentForDeliverableContainerMutation } from '~/core/routes/deliverables/[deliverableId]/mutations/NotifyNewCommentForDeliverableContainer.gql.generated';
import { useCreateThreadWithCommentMutation } from '~/packages/comments/mutations/CreateThreadWithComment.gql.generated';
import { EventLogDrawer } from '~/packages/event-logs/EventLogDrawer';

// Constants
const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'deliverable_container_thread_create',
];
const SINGLE_ENTITY_DRAWER_LABEL = 'Deliverable Activity';
const MULTI_ENTITY_DRAWER_LABEL = 'Deliverable Activity';

// Exports
type SingleEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
> & {
  entityId: string;
  receiptId?: string;
};
export const SingleEntityDrawer = (props: SingleEntityDrawerProps) => {
  const toast = useToast();
  const [createThread] = useCreateThreadWithCommentMutation();
  const [
    notifyNewCommentForDeliverableContainerMutation,
  ] = useNotifyNewCommentForDeliverableContainerMutation();
  return (
    <PermissionGuard
      permissions={(ctx) => ctx.flags.productsDeliverablesEventLogPermissions}
    >
      <EventLogDrawer
        {...props}
        entityThreadDomain="deliverables_container"
        entityIds={[props.entityId]}
        eventLogTypes={EVENT_LOG_TYPES}
        header={SINGLE_ENTITY_DRAWER_LABEL}
        triggerButtonProps={{ 'aria-label': SINGLE_ENTITY_DRAWER_LABEL }}
        onCreateThread={async ({ comment, participatingOrganizationIds }) => {
          try {
            const result = await createThread({
              variables: {
                comment,
                domain: 'deliverables_container',
                domainIdentifier: props.entityId,
                isPinned: false,
                participatingOrganizationIds,
              },
              refetchQueries: ['GetEventLogs'],
            });
            if (!result.data) throw new Error('Failed to create thread');
            const commentId = result.data?.thread.comments[0].id;
            if (!commentId) throw new Error('Failed to create comment');

            await notifyNewCommentForDeliverableContainerMutation({
              variables: {
                containerId: props.entityId,
                commentId,
                receiptId: props.receiptId,
              },
            });
          } catch (error) {
            Sentry.captureException(error);
            toast({
              title: 'Failed to create thread',
              status: 'error',
              position: 'top',
            });
          }
        }}
      />
    </PermissionGuard>
  );
};

type MultiEntityDrawerProps = Partial<
  Omit<React.ComponentProps<typeof EventLogDrawer>, 'entityIds'>
>;
export const MultiEntityDrawer = (props: MultiEntityDrawerProps) => (
  <PermissionGuard
    permissions={(ctx) =>
      ctx.flags.productsDeliverablesEventLogMultiEntityPermissions
    }
  >
    <EventLogDrawer
      {...props}
      entityIds={undefined}
      eventLogTypes={EVENT_LOG_TYPES}
      entityThreadDomain="deliverables_container"
      header={MULTI_ENTITY_DRAWER_LABEL}
      triggerButtonProps={{ 'aria-label': MULTI_ENTITY_DRAWER_LABEL }}
    />
  </PermissionGuard>
);
